import axios from "axios";
import { enqueue, allItems, removeItem, newKey, count as queueCount } from "./offlineQueue";
import { isOnline } from "./net";

export const API_URL =
  process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";

const API = axios.create({ baseURL: API_URL });

// Resolve menu image URLs (absolute / data: pass through, relative get host).
export const resolveImageUrl = (url) => {
  if (!url) return null;
  if (/^(https?:|data:)/i.test(url)) return url;
  return `${API_URL.replace(/\/$/, "")}${url.startsWith("/") ? "" : "/"}${url}`;
};

// --- Token handling --------------------------------------------------
const TOKEN_KEY = "menulink_token";
const USER_KEY = "menulink_user";
const ROLE_KEY = "menulink_role";
const FEATURES_KEY = "menulink_features";
const STAFF_KEY = "menulink_staff";
const ADMIN_KEY = "menulink_is_admin";
const ADMIN_CAPS_KEY = "menulink_admin_caps";
const STAFF_PERMS_KEY = "menulink_staff_perms";
const VENUE_KEY = "menulink_venue_type";
const MFA_PENDING_KEY = "menulink_mfa_pending";

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => { if (t) localStorage.setItem(TOKEN_KEY, t); };
// MFA enrollment gate: true when a privileged account must enable 2FA before
// the session is usable (server policy MFA_REQUIRED_ROLES). Drives the forced
// redirect to /security and the mandatory banner there.
export const mfaEnrollmentRequired = () => localStorage.getItem(MFA_PENDING_KEY) === "1";
export const setMfaPending = (v) => {
  if (v) localStorage.setItem(MFA_PENDING_KEY, "1");
  else localStorage.removeItem(MFA_PENDING_KEY);
};
// ---- Account security (SEC-1) ----
export const fetchSecurity = () => API.get("/auth/security").then((r) => r.data);
export const changePassword = (current_password, new_password) =>
  API.post("/auth/change-password", { current_password, new_password }).then((r) => { setToken(r.data.access_token); return r.data; });
export const signOutAllSessions = () =>
  API.post("/auth/sign-out-all").then((r) => { setToken(r.data.access_token); return r.data; });
export const setup2fa = () => API.post("/auth/2fa/setup").then((r) => r.data);
export const enable2fa = (code) => API.post("/auth/2fa/enable", { code }).then((r) => {
  // Enabling 2FA upgrades a restricted enrollment session to a full one: the
  // server returns a fresh token WITHOUT the mfa_pending claim. Swap it in and
  // clear the local gate so the user can use the dashboard normally.
  if (r.data && r.data.access_token) setToken(r.data.access_token);
  setMfaPending(false);
  return r.data;
});
export const disable2fa = (code) => API.post("/auth/2fa/disable", { code }).then((r) => r.data);
export const getRole = () => localStorage.getItem(ROLE_KEY);
export const getFeatures = () => {
  try { return JSON.parse(localStorage.getItem(FEATURES_KEY) || "[]"); }
  catch { return []; }
};
export const hasFeature = (f) => getFeatures().includes(f);
export const isAdmin = () => localStorage.getItem(ADMIN_KEY) === "1";
export const getAdminCaps = () => {
  try { return JSON.parse(localStorage.getItem(ADMIN_CAPS_KEY) || "[]"); }
  catch { return []; }
};
export const hasCapability = (c) => getAdminCaps().includes(c);
export const getStaffPermissions = () => {
  try { return JSON.parse(localStorage.getItem(STAFF_PERMS_KEY) || "[]"); }
  catch { return []; }
};
export const hasStaffPermission = (p) => getStaffPermissions().includes(p);
export const getTier = () => {
  try { return JSON.parse(localStorage.getItem(USER_KEY) || "null")?.subscription_tier || "free"; }
  catch { return "free"; }
};
// Per-tenant ISO-4217 currency code, read from the saved login session.
// Populated by saveSession() from RestaurantResponse.currency. Falls back
// to ETB which is the safer default for an Ethiopia-first product.
export const getCurrency = () => {
  try { return JSON.parse(localStorage.getItem(USER_KEY) || "null")?.currency || "ETB"; }
  catch { return "ETB"; }
};
export const getUser = () => {
  try {
    return JSON.parse(localStorage.getItem(USER_KEY) || "null");
  } catch {
    return null;
  }
};

export const saveSession = ({ token, role, user, features, isAdmin: adminFlag, venueType }) => {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(ROLE_KEY, role);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
  localStorage.setItem(FEATURES_KEY, JSON.stringify(features || []));
  localStorage.setItem(ADMIN_KEY, adminFlag ? "1" : "0");
  localStorage.setItem(VENUE_KEY, venueType || "hotel");
};

// Venue type drives the dashboard experience: "hotel" | "restaurant" | "spa_gym".
export const getVenueType = () => localStorage.getItem(VENUE_KEY) || "hotel";
// Where a manager lands: a spa/gym opens on its own dashboard (no hotel home).
export const managerHomePath = () => (getVenueType() === "spa_gym" ? "/gym-home" : "/home");
export const fetchGymDashboard = () => API.get("/services/dashboard").then((r) => r.data);

export const getStaffProfile = () => {
  try { return JSON.parse(localStorage.getItem(STAFF_KEY) || "null"); }
  catch { return null; }
};

// Wipe the offline read-cache (keys prefixed "odra_cache_"). The cache is NOT
// namespaced per tenant, so it MUST be cleared whenever the session changes —
// otherwise the previous tenant's cached lists (tables, reservations, etc.)
// would render for the next account signed in on the same browser. RLS already
// blocks any real cross-tenant *data* access; this stops stale cross-tenant
// data appearing in the UI (and stops actions on it failing under RLS).
export const clearOfflineCache = () => {
  try {
    Object.keys(localStorage)
      .filter((k) => k.startsWith("odra_cache_"))
      .forEach((k) => localStorage.removeItem(k));
  } catch (e) { /* private mode / quota */ }
};

export const clearSession = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(ROLE_KEY);
  localStorage.removeItem(FEATURES_KEY);
  localStorage.removeItem(ADMIN_KEY);
  localStorage.removeItem(ADMIN_CAPS_KEY);
  localStorage.removeItem(STAFF_PERMS_KEY);
  localStorage.removeItem(STAFF_KEY);
  localStorage.removeItem(VENUE_KEY);
  localStorage.removeItem(MFA_PENDING_KEY);
  clearOfflineCache();
};

API.interceptors.request.use((config) => {
  const token = getToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

API.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      clearSession();
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
    }
    // MFA gate: a role-restricted endpoint refused a not-yet-enrolled privileged
    // session. Keep the session (they're still authenticated) but force them to
    // the security page to finish enrolling.
    if (err?.response?.status === 403 && err?.response?.data?.detail === "mfa_enrollment_required") {
      setMfaPending(true);
      if (window.location.pathname !== "/security") {
        window.location.href = "/security";
      }
    }
    return Promise.reject(err);
  }
);

// --- Auth ------------------------------------------------------------
export const login = async (email, password, otp) => {
  // No role sent — backend uses the user's actual DB role. The login form
  // used to take a role dropdown but it was cosmetic (friend-test #76).
  // otp is only sent when the account has 2FA on (LoginPage prompts for it).
  const res = await API.post("/auth/login", { email, password, ...(otp ? { otp } : {}) });
  // New session (possibly a different tenant on this browser): drop any cached
  // data from a previous account before we prewarm the new one.
  clearOfflineCache();
  // MFA policy: privileged accounts without 2FA get a restricted session and
  // must enroll first. Record the gate so the app can force them to /security.
  setMfaPending(!!res.data.mfa_enrollment_required);
  saveSession({
    token: res.data.access_token,
    role: res.data.role,
    user: res.data.restaurant,
    features: res.data.features || [],
    isAdmin: !!res.data.is_admin,
    venueType: res.data.venue_type || "hotel",
  });
  // The logged-in person's OWN identity (staff account). Stored apart
  // from USER_KEY (which holds the restaurant) so refreshMe() does not
  // clobber it. Lets the sidebar show "who am I" with an avatar.
  localStorage.setItem(
    STAFF_KEY,
    JSON.stringify({
      name: res.data.staff_name || null,
      photo_url: res.data.staff_photo_url || null,
      role: res.data.role || null,
    })
  );
  try { prewarmOfflineCache(); } catch (e) { /* offline cache is best-effort */ }
  return res.data;
};

export const refreshMe = async () => {
  const res = await API.get("/auth/me");
  const user = {
    id: res.data.id,
    name: res.data.name,
    email: res.data.email,
    subscription_tier: res.data.subscription_tier,
    country: res.data.country || "ET",
    // Admin-set per-tenant language list (e.g. ["en","am"] for an Addis
    // hotel that opted out of Afaan Oromo). Cached here so manager-side
    // UI (ManagerMenuPage, etc.) can gate language inputs without an
    // extra round-trip. null means "use country defaults".
    supported_languages: Array.isArray(res.data.supported_languages)
      ? res.data.supported_languages
      : null,
  };
  localStorage.setItem(USER_KEY, JSON.stringify(user));
  localStorage.setItem(FEATURES_KEY, JSON.stringify(res.data.features || []));
  localStorage.setItem(VENUE_KEY, res.data.venue_type || "hotel");
  if (res.data.role) localStorage.setItem(ROLE_KEY, res.data.role);
  localStorage.setItem(ADMIN_KEY, res.data.is_admin ? "1" : "0");
  localStorage.setItem(ADMIN_CAPS_KEY, JSON.stringify(res.data.admin_capabilities || []));
  localStorage.setItem(STAFF_PERMS_KEY, JSON.stringify(res.data.staff_permissions || []));
  // Keep the MFA gate in sync on refresh/reload so it survives a page reload.
  setMfaPending(!!res.data.mfa_enrollment_required);
  try { prewarmOfflineCache(); } catch (e) { /* offline cache is best-effort */ }
  return res.data;
};

// Manager updates restaurant-level settings (country, etc.). Returns
// the fresh /auth/me payload so caller can update localStorage.
export const updateRestaurantSettings = async (patch) => {
  const res = await API.patch("/auth/me/settings", patch);
  const user = {
    id: res.data.id,
    name: res.data.name,
    email: res.data.email,
    subscription_tier: res.data.subscription_tier,
    country: res.data.country || "ET",
  };
  localStorage.setItem(USER_KEY, JSON.stringify(user));
  return res.data;
};

export const register = async (name, email, password) => {
  const res = await API.post("/auth/register", { name, email, password });
  return res.data;
};

// --- Orders ----------------------------------------------------------
export const fetchOrders = async ({ activeOnly = false, todayOnly = false, station = null } = {}) => {
  const _params = {};
  if (activeOnly) _params.active_only = true;
  if (todayOnly) _params.today_only = true;
  if (station) _params.station = station;
  const res = await API.get("/order/", {
    params: _params,
  });
  return res.data;
};

export const updateItemStatus = async (orderId, itemId, status) => {
  const res = await API.patch(`/order/${orderId}/items/${itemId}/status`, { status });
  return res.data;
};

export const updateOrderStatus = async (orderId, status, reason) => {
  const res = await API.patch(`/order/${orderId}/status`, { status, reason });
  return res.data;
};

// Waiter / manager places an order at the tableside via the dashboard.
// items: [{ menu_id, quantity, notes?, modifiers? }]
export const placeStaffOrder = async ({ tableId, items, notes, requestedAt }) => {
  const res = await API.post("/order/staff-placed", {
    table_id: tableId,
    notes: notes || null,
    requested_at: requestedAt || null,   // ISO string, or null = ASAP
    items: items.map((i) => {
      const out = { menu_id: i.menu_id, quantity: i.quantity };
      if (i.notes) out.notes = i.notes;
      if (i.modifiers && i.modifiers.length) out.modifiers = i.modifiers;
      return out;
    }),
  });
  return res.data;
};

// Reschedule (or clear -> ASAP) a still-pending timed order from the kitchen.
export const rescheduleOrder = async (orderId, requestedAt) => {
  const res = await API.patch(`/order/${orderId}/schedule`, { requested_at: requestedAt || null });
  return res.data;
};

// --- Tabs (table sessions) ------------------------------------------
export const fetchOpenTabs = () =>
  _cachedGet("open_tabs", () => API.get("/tabs/open").then((r) => r.data));

export const fetchTab = (tabId) =>
  API.get(`/tabs/${tabId}`).then((r) => r.data);

export const closeTab = (tabId) =>
  API.post(`/tabs/${tabId}/close`).then((r) => r.data);

// ---- Payments (Phase 1: manual Telebirr / CBE Birr recording) ----
// Offline-aware (Directive 1142/2026): if the connection is down, the settle is
// queued with an idempotency key and replayed on reconnect — never double-posts.
export const recordTabPayment = (tabId, body) =>
  _postOrQueue({ path: `/payments/tab/${tabId}`, body: body || {}, kind: "tab_payment", label: `Bill — tab #${tabId}` });
// Split a tab: settle ONLY the selected line items and issue their own receipt.
// Returns the updated serialized tab (with per-item settled flags + balance).
export const splitTabPayment = (tabId, body) =>
  API.post(`/payments/tab/${tabId}/split`, body).then((r) => r.data);
export const setTabDiscount = (tabId, body) => API.post(`/tabs/${tabId}/discount`, body).then((r) => r.data);
// Charge an in-house guest's F&B tab to their room folio (posts pre-tax net).
export const chargeTabToRoom = (tabId, reservationId) =>
  API.post(`/tabs/${tabId}/charge-to-room`, { reservation_id: reservationId }).then((r) => r.data);
// Lightweight in-house (checked-in) guest list for pickers.
export const fetchInhouseGuestsLite = () =>
  API.get("/reservations/inhouse-lite").then((r) => r.data);
// Charge an F&B tab to a group block's MASTER bill (posts the tab GROSS total,
// added to the block once — the block does not re-tax it).
export const chargeTabToGroup = (tabId, blockId) =>
  API.post(`/tabs/${tabId}/charge-to-group`, { block_id: blockId }).then((r) => r.data);
// Lightweight list of OPEN group/company blocks for pickers.
export const fetchOpenBlocksLite = () =>
  API.get("/reservations/blocks-lite").then((r) => r.data);
export const reopenTab = (tabId, body) => API.post(`/tabs/${tabId}/reopen`, body || {}).then((r) => r.data);
// Bill transfer: relocate selected UNSETTLED items to another table's tab.
// No ledger posting — the items are simply paid on the target tab later.
// body: { item_ids: number[], target_table_id: number }. Returns { source, target }.
export const transferTabItems = (tabId, body) =>
  API.post(`/tabs/${tabId}/transfer-items`, body).then((r) => r.data);
export const fetchAuditLog = (params) => API.get("/audit", { params: params || {} }).then((r) => r.data);
export const fetchManagerHome = () => API.get("/home").then((r) => r.data);
export const fetchCostChecklist = () => API.get("/cost/checklist").then((r) => r.data);
// Front Desk — rooms & housekeeping (Slice 1)
export const fetchRooms = () => _cachedGet("rooms", () => API.get("/front-desk/rooms").then((r) => r.data));
export const createRoom = (body) => API.post("/front-desk/rooms", body).then((r) => r.data);
export const updateRoom = (id, body) => API.patch(`/front-desk/rooms/${id}`, body).then((r) => r.data);
export const deleteRoom = (id) => API.delete(`/front-desk/rooms/${id}`).then((r) => r.data);
export const fetchRoomTypes = () => API.get("/front-desk/room-types").then((r) => r.data);
export const createRoomType = (body) => API.post("/front-desk/room-types", body).then((r) => r.data);
export const updateRoomType = (id, body) => API.patch(`/front-desk/room-types/${id}`, body).then((r) => r.data);
export const deleteRoomType = (id) => API.delete(`/front-desk/room-types/${id}`).then((r) => r.data);
export const fetchRoomsSummary = () => API.get("/front-desk/summary").then((r) => r.data);
export const fetchCleaners = () => API.get("/front-desk/cleaners").then((r) => r.data);
export const fetchHousekeepingAttention = () => API.get("/front-desk/housekeeping/attention").then((r) => r.data);
// Front Desk — rate plans (Phase A1: room type x occupancy x meal plan)
export const fetchRatePlans = () => API.get("/front-desk/rate-plans").then((r) => r.data);
export const createRatePlan = (body) => API.post("/front-desk/rate-plans", body).then((r) => r.data);
export const updateRatePlan = (id, body) => API.patch(`/front-desk/rate-plans/${id}`, body).then((r) => r.data);
export const deleteRatePlan = (id) => API.delete(`/front-desk/rate-plans/${id}`).then((r) => r.data);
export const resolveRatePlan = (roomTypeId, occupancy, mealPlan) => API.get("/front-desk/rate-plans/resolve", { params: { room_type_id: roomTypeId, occupancy, meal_plan: mealPlan } }).then((r) => r.data);
// Front Desk — company accounts / city ledger (Phase A4)
export const fetchCompanies = () => API.get("/front-desk/companies").then((r) => r.data);
export const createCompany = (body) => API.post("/front-desk/companies", body).then((r) => r.data);
export const updateCompany = (id, body) => API.patch(`/front-desk/companies/${id}`, body).then((r) => r.data);
export const deleteCompany = (id) => API.delete(`/front-desk/companies/${id}`).then((r) => r.data);
export const fetchCompanyStatement = (id) => API.get(`/reservations/companies/${id}/statement`).then((r) => r.data);
export const recordCompanyAdvance = (id, body) => API.post(`/front-desk/companies/${id}/advance`, body).then((r) => r.data);
export const recordCompanyRefund = (id, body) => API.post(`/front-desk/companies/${id}/refund`, body).then((r) => r.data);
// ---- Support tickets (in-app incident reporting) ----
export const fetchTickets = () => API.get("/support/tickets").then((r) => r.data);
export const fetchTicket = (id) => API.get(`/support/tickets/${id}`).then((r) => r.data);
export const createTicket = (body) => API.post("/support/tickets", body).then((r) => r.data);
export const addTicketMessage = (id, msg) => API.post(`/support/tickets/${id}/messages`, { body: msg }).then((r) => r.data);
export const setTicketStatus = (id, status) => API.patch(`/support/tickets/${id}`, { status }).then((r) => r.data);
// ---- Admin: internal team & capabilities ----
export const adminWhoami = () => API.get("/admin/me").then((r) => r.data);
export const fetchTeamMeta = () => API.get("/admin/team/meta").then((r) => r.data);
export const fetchTeam = () => API.get("/admin/team").then((r) => r.data);
export const createTeamMember = (body) => API.post("/admin/team", body).then((r) => r.data);
export const updateTeamMember = (id, body) => API.patch(`/admin/team/${id}`, body).then((r) => r.data);
export const deleteTeamMember = (id) => API.delete(`/admin/team/${id}`).then((r) => r.data);

export const adminFetchTickets = (status) => API.get("/support/admin/tickets", { params: { ...(status ? { status } : {}) } }).then((r) => r.data);
export const adminFetchTicket = (id) => API.get(`/support/admin/tickets/${id}`).then((r) => r.data);
export const adminReplyTicket = (id, msg) => API.post(`/support/admin/tickets/${id}/messages`, { body: msg }).then((r) => r.data);
export const adminSetTicketStatus = (id, status) => API.patch(`/support/admin/tickets/${id}`, { status }).then((r) => r.data);

export const fetchProformas = (companyId) => API.get("/front-desk/proformas", { params: { ...(companyId ? { company_account_id: companyId } : {}) } }).then((r) => r.data);
export const fetchProforma = (id) => API.get(`/front-desk/proformas/${id}`).then((r) => r.data);
export const createProforma = (body) => API.post("/front-desk/proformas", body).then((r) => r.data);
export const updateProformaStatus = (id, status) => API.patch(`/front-desk/proformas/${id}`, { status }).then((r) => r.data);
export const recordProformaPayment = (id, body) => API.post(`/front-desk/proformas/${id}/record-payment`, body).then((r) => r.data);
// Front Desk — partner hotels (outsourced / overflow rooms)
export const fetchPartnerHotels = (includeInactive = false) => API.get("/partner-hotels", { params: { ...(includeInactive ? { include_inactive: true } : {}) } }).then((r) => r.data);
export const createPartnerHotel = (body) => API.post("/partner-hotels", body).then((r) => r.data);
export const updatePartnerHotel = (id, body) => API.patch(`/partner-hotels/${id}`, body).then((r) => r.data);
export const deletePartnerHotel = (id) => API.delete(`/partner-hotels/${id}`).then((r) => r.data);
export const fetchPartnerPayables = () => API.get("/partner-hotels/payables").then((r) => r.data);
export const recordPartnerPayment = (id, body) => API.post(`/partner-hotels/${id}/payments`, body).then((r) => r.data);
export const fetchOutsourcedReport = ({ from, to } = {}) => API.get("/reservations/outsourced-report", { params: { ...(from ? { from } : {}), ...(to ? { to } : {}) } }).then((r) => r.data);
// Front Desk — forex report (Phase B1)
export const fetchForexReport = (from, to) => API.get("/reservations/forex-report", { params: { from, ...(to ? { to } : {}) } }).then((r) => r.data);
export const fetchMgmtReport = (from, to) => API.get("/reservations/mgmt-report", { params: { from, ...(to ? { to } : {}) } }).then((r) => r.data);
export const fetchFrontDeskSnapshot = (from, to) => API.get("/reservations/snapshot", { params: { ...(from ? { from } : {}), ...(to ? { to } : {}) } }).then((r) => r.data);
export const fetchGuestHistory = (params) => API.get("/reservations/guest-history", { params: params || {} }).then((r) => r.data);
export const searchGuests = (q) => API.get("/reservations/guest-search", { params: { q } }).then((r) => r.data);
// Front Desk — shift handover (D2, add-on: shift_handover)
export const fetchHandovers = () => API.get("/front-desk/handovers").then((r) => r.data);
export const createHandover = (body) => API.post("/front-desk/handovers", body).then((r) => r.data);
export const acknowledgeHandover = (id) => API.post(`/front-desk/handovers/${id}/acknowledge`).then((r) => r.data);
// Front Desk — lost & found register
export const fetchLostFound = ({ status, q } = {}) => API.get("/lost-found", { params: { ...(status ? { status } : {}), ...(q ? { q } : {}) } }).then((r) => r.data);
export const createLostFound = (body) => API.post("/lost-found", body).then((r) => r.data);
export const updateLostFound = (id, patch) => API.patch(`/lost-found/${id}`, patch).then((r) => r.data);
export const deleteLostFound = (id) => API.delete(`/lost-found/${id}`).then((r) => r.data);
// Front Desk — maintenance tickets (lightweight room-repair log)
export const fetchMaintenance = ({ status } = {}) => API.get("/maintenance", { params: { ...(status ? { status } : {}) } }).then((r) => r.data);
export const createMaintenance = (body) => API.post("/maintenance", body).then((r) => r.data);
export const resolveMaintenance = (id, { notes } = {}) => API.post(`/maintenance/${id}/resolve`, { notes }).then((r) => r.data);
// Front Desk — check-in upsell (D3, add-on: fd_upsell)
export const fetchUpsellOptions = (id) => API.get(`/reservations/${id}/upsell-options`).then((r) => r.data);
export const addUpsell = (id, body) => API.post(`/reservations/${id}/upsell`, body).then((r) => r.data);
// Front Desk — post-stay guest satisfaction (D6, unified with ratings)
export const addStayFeedback = (id, body) => API.post(`/reservations/${id}/feedback`, body).then((r) => r.data);
export const fetchStayFeedback = (from, to) => API.get("/reservations/feedback-summary", { params: { ...(from ? { from } : {}), ...(to ? { to } : {}) } }).then((r) => r.data);
// Front Desk — pre-arrival / online check-in link (D7, add-on: online_checkin)
export const getPrecheckinLink = (id) => API.post(`/reservations/${id}/precheckin-link`).then((r) => r.data);
export const getReservationIdImage = (id) => API.get(`/reservations/${id}/id-image`).then((r) => r.data);
// Front Desk — guest messaging over Telegram (D8, add-on: guest_messaging)
export const getMessagingStatus = () => API.get("/reservations/messaging/status").then((r) => r.data);
export const setupMessaging = (bot_token) => API.post("/reservations/messaging/setup", { bot_token }).then((r) => r.data);
export const getOptInLink = (id) => API.get(`/reservations/${id}/opt-in-link`).then((r) => r.data);
export const sendPrecheckinMessage = (id) => API.post(`/reservations/${id}/send-precheckin`).then((r) => r.data);
export const sendGuestMessage = (id, body) => API.post(`/reservations/${id}/message`, body).then((r) => r.data);
export const fetchGuestMessages = (id) => API.get(`/reservations/${id}/messages`).then((r) => r.data);
// R3 — owner data export (CSV downloads through the authenticated API)
const _downloadBlob = (data, filename) => {
  const url = window.URL.createObjectURL(new Blob([data]));
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  window.URL.revokeObjectURL(url);
};
export const exportReservationsCsv = () => API.get("/export/reservations.csv", { responseType: "blob" }).then((r) => _downloadBlob(r.data, "odra_reservations.csv"));
export const exportOrdersCsv = () => API.get("/export/orders.csv", { responseType: "blob" }).then((r) => _downloadBlob(r.data, "odra_orders.csv"));
export const exportAccountingCsv = (from, to) => API.get("/export/accounting.csv", { params: { ...(from ? { from } : {}), ...(to ? { to } : {}) }, responseType: "blob" }).then((r) => _downloadBlob(r.data, `odra_accounting_${from || ""}_${to || ""}.csv`));

// --- Data protection (Proclamation 1321/2024) -----------------------
export const exportGuestData = (rid) => API.get(`/reservations/${rid}/data-export`).then((r) => r.data);
export const eraseGuestData = (rid, reason) => API.post(`/reservations/${rid}/erase-guest`, { reason: reason || null }).then((r) => r.data);
export const runRetention = (dryRun = true) => API.post("/reservations/retention/run", { dry_run: dryRun }).then((r) => r.data);
// Services & Memberships (SM0, add-on: services)
export const fetchServices = (area) => API.get("/services", { params: area ? { area } : {} }).then((r) => r.data);
export const createService = (body) => API.post("/services", body).then((r) => r.data);
export const updateService = (id, body) => API.patch(`/services/${id}`, body).then((r) => r.data);
export const deleteService = (id) => API.delete(`/services/${id}`).then((r) => r.data);
export const bookService = (id, body) => API.post(`/services/${id}/book`, body).then((r) => r.data);
export const fetchServiceBookings = (area) => API.get("/services/bookings", { params: area ? { area } : {} }).then((r) => r.data);
export const updateServiceBooking = (id, body) => API.patch(`/services/bookings/${id}`, body).then((r) => r.data);
export const fetchServicesSummary = () => API.get("/services/summary").then((r) => r.data);
export const fetchServiceBookingReceipt = (bid) => API.get(`/services/bookings/${bid}/receipt`).then((r) => r.data);
// SM5a — scheduler resources (therapists / rooms)
export const fetchResources = () => API.get("/services/resources").then((r) => r.data);
export const createResource = (body) => API.post("/services/resources", body).then((r) => r.data);
export const updateResource = (id, body) => API.patch(`/services/resources/${id}`, body).then((r) => r.data);
export const deleteResource = (id) => API.delete(`/services/resources/${id}`).then((r) => r.data);
// SM5b/c — scheduler
export const scheduleAppointment = (body) => API.post("/services/schedule", body).then((r) => r.data);
export const fetchSchedule = (on) => API.get("/services/schedule", { params: on ? { on } : {} }).then((r) => r.data);
export const completeBooking = (id, body) => API.post(`/services/bookings/${id}/complete`, body || {}).then((r) => r.data);
export const noShowBooking = (id, body) => API.post(`/services/bookings/${id}/no-show`, body || {}).then((r) => r.data);
// SM2 — memberships
export const fetchMembers = () => API.get("/services/members").then((r) => r.data);
export const gymCheckin = (body) => API.post("/services/checkin", body).then((r) => r.data);
export const fetchRenewals = (params = {}) => API.get("/services/renewals", { params }).then((r) => r.data);
// Group classes (capacity + waitlist)
export const fetchClasses = (params = {}) => API.get("/services/classes", { params }).then((r) => r.data);
export const fetchClass = (id) => API.get(`/services/classes/${id}`).then((r) => r.data);
export const createClass = (body) => API.post("/services/classes", body).then((r) => r.data);
export const updateClass = (id, body) => API.patch(`/services/classes/${id}`, body).then((r) => r.data);
export const enrollClass = (id, body) => API.post(`/services/classes/${id}/enroll`, body).then((r) => r.data);
export const cancelEnrollment = (cid, eid) => API.post(`/services/classes/${cid}/enrollments/${eid}/cancel`).then((r) => r.data);
export const attendEnrollment = (cid, eid) => API.post(`/services/classes/${cid}/enrollments/${eid}/attend`).then((r) => r.data);
// Packages / session credits
export const fetchPackageTemplates = () => API.get("/services/package-templates").then((r) => r.data);
export const createPackageTemplate = (body) => API.post("/services/package-templates", body).then((r) => r.data);
export const updatePackageTemplate = (id, body) => API.patch(`/services/package-templates/${id}`, body).then((r) => r.data);
export const sellPackage = (body) => API.post("/services/packages", body).then((r) => r.data);
export const fetchPackages = (params = {}) => API.get("/services/packages", { params }).then((r) => r.data);
export const redeemPackage = (id, n = 1) => API.post(`/services/packages/${id}/redeem`, null, { params: { n } }).then((r) => r.data);
export const fetchGymAttendance = (params = {}) => API.get("/services/attendance", { params }).then((r) => r.data);
export const createMember = (body) => API.post("/services/members", body).then((r) => r.data);
export const getMember = (id) => API.get(`/services/members/${id}`).then((r) => r.data);
export const recordDues = (id, body) => API.post(`/services/members/${id}/dues`, body).then((r) => r.data);
export const remindMember = (id) => API.post(`/services/members/${id}/remind`).then((r) => r.data);
export const updateMember = (id, body) => API.patch(`/services/members/${id}`, body).then((r) => r.data);
// SM2b — membership plan catalog
export const fetchMembershipPlans = () => API.get("/services/membership-plans").then((r) => r.data);
export const createMembershipPlan = (body) => API.post("/services/membership-plans", body).then((r) => r.data);
export const updateMembershipPlan = (id, body) => API.patch(`/services/membership-plans/${id}`, body).then((r) => r.data);
export const deleteMembershipPlan = (id) => API.delete(`/services/membership-plans/${id}`).then((r) => r.data);
export const fetchRoomAvailability = (from, to, roomTypeId, excludeReservationId) => API.get("/front-desk/rooms/availability", { params: { from, ...(to ? { to } : {}), ...(roomTypeId ? { room_type_id: roomTypeId } : {}), ...(excludeReservationId ? { exclude_reservation_id: excludeReservationId } : {}), _ts: Date.now() } }).then((r) => r.data);
// --- Offline read cache (Directive 1142/2026) ------------------------
// Remember the last successful result of a GET so the screen still renders the
// last-synced data during an outage — otherwise a list the user needs to act on
// (in-house guests to check out, open tabs to bill) would be blank offline.
const _readCache = (key) => {
  try { const s = localStorage.getItem("odra_cache_" + key); return s ? JSON.parse(s) : null; }
  catch (e) { return null; }
};
const _writeCache = (key, val) => {
  try { localStorage.setItem("odra_cache_" + key, JSON.stringify(val)); } catch (e) { /* quota / private mode */ }
};
async function _cachedGet(key, run) {
  if (!isOnline()) {
    const c = _readCache(key);
    if (c !== null) return c;                 // offline: serve last-synced snapshot
  }
  try {
    const data = await run();
    _writeCache(key, data);
    return data;
  } catch (e) {
    if (!e || !e.response) {                   // network error — fall back to cache
      const c = _readCache(key);
      if (c !== null) return c;
    }
    throw e;
  }
}

// Front Desk — reservations (Slice 2a)
export const fetchReservations = (view, on) =>
  _cachedGet(`reservations_${view || "all"}_${on || ""}`,
    () => API.get("/reservations", { params: { view: view || "all", ...(on ? { on } : {}) } }).then((r) => r.data));
export const fetchReservation = (id) => API.get(`/reservations/${id}`).then((r) => r.data);
export const createReservation = (body) => API.post("/reservations", body).then((r) => r.data);
export const updateReservation = (id, body) => API.patch(`/reservations/${id}`, body).then((r) => r.data);
export const cancelReservation = (id, body) => API.post(`/reservations/${id}/cancel`, body || {}).then((r) => r.data);
export const noShowReservation = (id) => API.post(`/reservations/${id}/no-show`).then((r) => r.data);
export const reinstateReservation = (id) => API.post(`/reservations/${id}/reinstate`).then((r) => r.data);
export const moveRoom = (id, body) => API.post(`/reservations/${id}/move-room`, body).then((r) => r.data);
export const changeStay = (id, body) => API.post(`/reservations/${id}/change-stay`, body).then((r) => r.data);
export const fetchFrontDeskBoard = () =>
  _cachedGet("frontdesk_board", () => API.get("/reservations/board").then((r) => r.data));
export const setReservationDiscount = (id, body) => API.post(`/reservations/${id}/discount`, body).then((r) => r.data);
export const takeReservationPayment = (id, body) => API.post(`/reservations/${id}/payment`, body).then((r) => r.data);
// --- Offline-aware front-desk actions (Directive 1142/2026) ----------
// A "no response" error means the request never reached the server (offline /
// network drop) — those are safe to queue and replay. An error WITH a response
// (e.g. 409 room occupied, 422) is a real server decision and must surface to
// the caller, so it is re-thrown.
const _isNetworkError = (e) => !!e && !e.response;

async function _postOrQueue({ path, body, kind, label }) {
  const key = newKey();
  const payload = { ...(body || {}), idempotency_key: key };
  if (!isOnline()) {
    await enqueue({ kind, label, method: "post", path, body: payload });
    return { _queued: true, idempotency_key: key };
  }
  try {
    const r = await API.post(path, payload);
    return r.data;
  } catch (e) {
    if (_isNetworkError(e)) {
      await enqueue({ kind, label, method: "post", path, body: payload });
      return { _queued: true, idempotency_key: key };
    }
    throw e;
  }
}

export const checkInReservation = (id, body) =>
  _postOrQueue({ path: `/reservations/${id}/check-in`, body: body || {}, kind: "check_in", label: `Check-in #${id}` });
export const checkOutReservation = (id, body) =>
  _postOrQueue({ path: `/reservations/${id}/check-out`, body: body || {}, kind: "check_out", label: `Check-out #${id}` });

// Replay queued actions. 2xx = applied (or idempotent replay) → drop. A 4xx
// means the server rejected it for good (e.g. a room taken while offline) → drop
// and collect it so the UI can tell the user. Network error → keep for next try.
export async function flushOutbox() {
  if (!isOnline()) return { sent: 0, failed: 0, pending: await queueCount() };
  const items = await allItems();
  let sent = 0; const failures = [];
  for (const it of items) {
    try {
      await API.post(it.path, it.body);
      await removeItem(it.id);
      sent += 1;
    } catch (e) {
      if (_isNetworkError(e)) { break; }        // still offline — stop, retry later
      await removeItem(it.id);                   // server rejected — don't loop forever
      failures.push({ label: it.label, detail: e.response && e.response.data });
    }
  }
  if (failures.length) {
    try { window.dispatchEvent(new CustomEvent("odra:sync-failures", { detail: failures })); } catch (e) { /* noop */ }
  }
  return { sent, failed: failures.length, pending: await queueCount() };
}

// Pre-warm the offline cache (Option 1). While online, silently fetch the
// screens staff must be able to use during an outage so they're never blank —
// even ones the user hasn't opened yet this session. Each fetcher writes to the
// read-cache via _cachedGet; failures (wrong role / plan) are ignored. Requires
// a logged-in session; skipped when offline.
export function prewarmOfflineCache() {
  if (!isOnline() || !getToken()) return;
  const jobs = [
    () => fetchOpenTabs(),
    () => fetchFrontDeskBoard(),
    () => fetchReservations("inhouse"),
    () => fetchReservations("arrivals"),
    () => fetchReservations("departures"),
    () => fetchReservations("all"),
    () => fetchTables(),
    () => fetchRooms(),
  ];
  jobs.forEach((j) => { try { j().catch(() => {}); } catch (e) { /* ignore */ } });
}

// Start the offline background loop: on reconnect, flush the outbox and refresh
// the cache; also every 30s (flush) and ~3 min (keep-fresh) as a safety net.
let _syncStarted = false;
export function initOfflineSync() {
  if (_syncStarted) return;
  _syncStarted = true;
  const flush = () => { flushOutbox().catch(() => {}); };
  const onReconnect = () => { flush(); prewarmOfflineCache(); };
  window.addEventListener("online", onReconnect);
  setInterval(flush, 30000);
  setInterval(prewarmOfflineCache, 180000);
  flush();
  prewarmOfflineCache();
}
export const fetchRoomsReport = (from, to) => API.get("/reservations/report", { params: { ...(from ? { from } : {}), ...(to ? { to } : {}) } }).then((r) => r.data);
export const fetchGuestList = (on, includeOutsourced) => API.get("/reservations/guest-list", { params: { ...(on ? { on } : {}), ...(includeOutsourced ? { include_outsourced: 1 } : {}) } }).then((r) => r.data);
export const addReservationCharge = (id, body) => API.post(`/reservations/${id}/charges`, body).then((r) => r.data);
export const deleteReservationCharge = (id, cid) => API.delete(`/reservations/${id}/charges/${cid}`).then((r) => r.data);
export const transferReservationCharge = (id, cid, targetId) => API.post(`/reservations/${id}/charges/${cid}/transfer`, { target_reservation_id: targetId }).then((r) => r.data);
// Guest approval of a room charge — front desk records a signed printed slip, or
// asks the Telegram-connected guest to tap-to-approve.
export const markChargeSigned = (id, cid) => API.post(`/reservations/${id}/charges/${cid}/mark-signed`).then((r) => r.data);
export const requestChargeApproval = (id, cid) => API.post(`/reservations/${id}/charges/${cid}/request-approval`).then((r) => r.data);
// Front Desk — guest services (Phase B3)
export const fetchGuestServices = (id) => API.get(`/reservations/${id}/services`).then((r) => r.data);
export const addGuestService = (id, body) => API.post(`/reservations/${id}/services`, body).then((r) => r.data);
export const updateGuestService = (id, sid, body) => API.patch(`/reservations/${id}/services/${sid}`, body).then((r) => r.data);
export const deleteGuestService = (id, sid) => API.delete(`/reservations/${id}/services/${sid}`).then((r) => r.data);
// Front Desk — events & banquets (Phase C)
export const fetchVenues = () => API.get("/events/venues").then((r) => r.data);
export const createVenue = (body) => API.post("/events/venues", body).then((r) => r.data);
export const updateVenue = (id, body) => API.patch(`/events/venues/${id}`, body).then((r) => r.data);
export const deleteVenue = (id) => API.delete(`/events/venues/${id}`).then((r) => r.data);
export const fetchEvents = (params) => API.get("/events/events", { params: params || {} }).then((r) => r.data);
export const fetchEvent = (id) => API.get(`/events/events/${id}`).then((r) => r.data);
export const createEvent = (body) => API.post("/events/events", body).then((r) => r.data);
export const updateEvent = (id, body) => API.patch(`/events/events/${id}`, body).then((r) => r.data);
export const deleteEvent = (id) => API.delete(`/events/events/${id}`).then((r) => r.data);
export const addEventCharge = (id, body) => API.post(`/events/events/${id}/charges`, body).then((r) => r.data);
export const deleteEventCharge = (id, cid) => API.delete(`/events/events/${id}/charges/${cid}`).then((r) => r.data);
export const fetchEventsBoard = (from, to) => API.get("/events/board", { params: { ...(from ? { date_from: from } : {}), ...(to ? { date_to: to } : {}) } }).then((r) => r.data);
export const fetchEventPackages = () => API.get("/events/packages").then((r) => r.data);
export const fetchEventsReport = (from, to) => API.get("/events/report", { params: { ...(from ? { date_from: from } : {}), ...(to ? { date_to: to } : {}) } }).then((r) => r.data);
// Front Desk — night audit (Phase A5)
export const fetchNightAuditPreview = () => API.get("/reservations/night-audit/preview").then((r) => r.data);
export const closeNightAudit = (body) => API.post("/reservations/night-audit/close", body || {}).then((r) => r.data);
export const fetchReservationReceipt = (id) => API.get(`/reservations/${id}/receipt`).then((r) => r.data);
// Post-checkout Google review QR for the printed folio. Returns
// {url, qr_code_url} (both null when no Google review URL is configured).
export const fetchReviewLink = (id) => API.get(`/reservations/${id}/review-link`).then((r) => r.data);
// Front Desk — group/company blocks (Slice 2e)
export const createBlock = (body) => API.post("/reservations/blocks", body).then((r) => r.data);
export const fetchBlocks = () => API.get("/reservations/blocks").then((r) => r.data);
export const fetchBlock = (id) => API.get(`/reservations/blocks/${id}`).then((r) => r.data);
export const updateBlock = (id, body) => API.patch(`/reservations/blocks/${id}`, body).then((r) => r.data);
export const addBlockRoom = (id, body) => API.post(`/reservations/blocks/${id}/rooms`, body || {}).then((r) => r.data);
export const removeBlockRoom = (id, rid) => API.delete(`/reservations/blocks/${id}/rooms/${rid}`).then((r) => r.data);
export const blockPayment = (id, body) => API.post(`/reservations/blocks/${id}/payment`, body).then((r) => r.data);
export const cancelBlock = (id) => API.post(`/reservations/blocks/${id}/cancel`).then((r) => r.data);
export const blockCheckIn = (id) => API.post(`/reservations/blocks/${id}/check-in`).then((r) => r.data);
export const blockCheckOut = (id) => API.post(`/reservations/blocks/${id}/check-out`).then((r) => r.data);
export const fetchBlockReceipt = (id) => API.get(`/reservations/blocks/${id}/receipt`).then((r) => r.data);
// Group-level charges on a block's master bill (tax-inclusive, added once).
export const addBlockCharge = (bid, body) => API.post(`/reservations/blocks/${bid}/charges`, body).then((r) => r.data);
// Outsource one or more of a block's guests to a partner hotel. Provide EITHER
// partner_hotel_id OR partner_hotel_name (a name auto-creates/reuses a partner).
export const createBlockOutsource = (blockId, body) => API.post(`/reservations/blocks/${blockId}/outsource`, body).then((r) => r.data);
export const deleteBlockCharge = (bid, cid) => API.delete(`/reservations/blocks/${bid}/charges/${cid}`).then((r) => r.data);
export const fetchClosedTabs = (date) => API.get("/tabs/closed", { params: date ? { date } : {} }).then((r) => r.data);
export const fetchZReport = (date) => API.get("/tabs/z-report", { params: date ? { date } : {} }).then((r) => r.data);
// --- Finance reports (S1) — read-only ledger views over `postings` -------
// Manager + cashier. Each takes an optional business date (YYYY-MM-DD);
// the backend defaults to the venue's open business day. NOTE: the Z-report
// helper is named fetchFinanceZReport to avoid clashing with fetchZReport
// above (the older /tabs/z-report view).
export const fetchFinanceZReport = (date, { from, to } = {}) =>
  API.get("/finance-reports/z-report", {
    params: {
      ...(date ? { date } : {}),
      ...(from ? { from } : {}),
      ...(to ? { to } : {}),
    },
  }).then((r) => r.data);
export const fetchDocumentReport = (date, { from, to } = {}) =>
  API.get("/finance-reports/documents", {
    params: {
      ...(date ? { date } : {}),
      ...(from ? { from } : {}),
      ...(to ? { to } : {}),
    },
  }).then((r) => r.data);
export const voidDocument = (documentNumber, reason) =>
  API.post("/finance-reports/documents/void", { document_number: documentNumber, reason }).then((r) => r.data);
export const fetchVatReport = ({ from, to } = {}) =>
  API.get("/finance-reports/vat", {
    params: { ...(from ? { from } : {}), ...(to ? { to } : {}) },
  }).then((r) => r.data);
export const fetchSalesReport = ({ date, from, to, cashierId, scope, point } = {}) =>
  API.get("/finance-reports/sales", {
    params: {
      ...(date ? { date } : {}),
      ...(from ? { from } : {}),
      ...(to ? { to } : {}),
      ...(cashierId ? { cashier_id: cashierId } : {}),
      ...(point ? { point } : {}),
      ...(scope ? { scope } : {}),
    },
  }).then((r) => r.data);
// Salesperson (seller) sales report — totals per salesperson + a per-sale list.
export const fetchSalespersonReport = ({ date, from, to, salespersonId, point } = {}) =>
  API.get("/finance-reports/salesperson", {
    params: {
      ...(date ? { date } : {}),
      ...(from ? { from } : {}),
      ...(to ? { to } : {}),
      ...(salespersonId ? { salesperson_id: salespersonId } : {}),
      ...(point ? { point } : {}),
    },
  }).then((r) => r.data);
// --- Cashier till / drawer sessions -----------------------------------
// A cashier opens a till for their sales point with an opening float, rings
// sales (which post to the ledger tagged with their till), then closes it with
// a cash count → their own Z-read + variance. Manager sees all tills.
export const openTill = ({ point, openingFloat }) =>
  API.post("/tills/open", { point, opening_float: Number(openingFloat) || 0 }).then((r) => r.data);
export const fetchCurrentTill = (point) =>
  API.get("/tills/current", {
    params: { ...(point ? { point } : {}) },
  }).then((r) => r.data);
export const closeTill = (id, { countedCash, varianceReason }) =>
  API.post(`/tills/${id}/close`, {
    counted_cash: Number(countedCash) || 0,
    ...(varianceReason ? { variance_reason: varianceReason } : {}),
  }).then((r) => r.data);
export const fetchTillZ = (id) =>
  API.get(`/tills/${id}/z`).then((r) => r.data);
export const fetchTills = ({ businessDate, status } = {}) =>
  API.get("/tills", {
    params: {
      ...(businessDate ? { business_date: businessDate } : {}),
      ...(status ? { status } : {}),
    },
  }).then((r) => r.data);

export const fetchPaymentSettings = () =>
  API.get("/payments/settings").then((r) => r.data);
export const savePaymentSettings = (body) =>
  API.put("/payments/settings", body).then((r) => r.data);
export const uploadPaymentQr = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return API.post("/payments/qr", fd).then((r) => r.data);
};

// --- Menu (manager) --------------------------------------------------
export const fetchMenu = async () => {
  const res = await API.get("/menu/");
  return res.data;
};

export const createMenuItem = async (payload) => {
  const res = await API.post("/menu/", payload);
  return res.data;
};

export const updateMenuItem = async (id, patch) => {
  const res = await API.patch(`/menu/${id}`, patch);
  return res.data;
};

export const deleteMenuItem = async (id) => {
  await API.delete(`/menu/${id}`);
};

export const uploadMenuImage = async (id, file) => {
  const fd = new FormData();
  fd.append("file", file);
  const res = await API.post(`/menu/${id}/image`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
};

export const uploadStaffPhoto = async (id, file) => {
  const fd = new FormData();
  fd.append("file", file);
  const res = await API.post(`/staff/${id}/photo`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
};

// --- Tables (manager) ------------------------------------------------
export const fetchVisitQR = async () => {
  const res = await API.get("/tables/visit-qr");
  return res.data;
};

export const fetchTables = () =>
  _cachedGet("tables", () => API.get("/tables/").then((r) => r.data));

export const createTable = async (tableNumber, area, kind) => {
  const res = await API.post("/tables/", { table_number: tableNumber, area: area || null, kind: kind || "dining" });
  return res.data;
};

export const updateTable = async (id, body) => {
  const res = await API.patch(`/tables/${id}`, body);
  return res.data;
};

export const deleteTable = async (id) => {
  await API.delete(`/tables/${id}`);
};

// --- Staff (manager) -------------------------------------------------
export const fetchStaff = async () => {
  const res = await API.get("/staff/");
  return res.data;
};

export const createStaffUser = async (payload) => {
  const res = await API.post("/staff/", payload);
  return res.data;
};

export const updateStaffUser = async (id, patch) => {
  const res = await API.patch(`/staff/${id}`, patch);
  return res.data;
};

export const deleteStaffUser = async (id) => {
  await API.delete(`/staff/${id}`);
};

// --- Reports (manager) -----------------------------------------------
// Report range can be a preset string ("today"/"yesterday"/"3d"/"week"/
// "month"/"all") OR an object { start, end } (YYYY-MM-DD) for a custom
// continuous range. The backend resolves both.
const asParams = (range) =>
  typeof range === "string" ? { window: range } : (range || {});

export const fetchSummary = (range = "today") =>
  API.get("/reports/summary", { params: asParams(range) }).then((r) => r.data);

export const fetchTopItems = (range = "week", limit = 10) =>
  API.get("/reports/top-items", { params: { ...asParams(range), limit } }).then((r) => r.data);

export const fetchLowPerformers = (range = "month") =>
  API.get("/reports/low-performers", { params: asParams(range) }).then((r) => r.data);

export const fetchHourly = (range = "week") =>
  API.get("/reports/hourly", { params: asParams(range) }).then((r) => r.data);

export const fetchDaily = (range = "week") =>
  API.get("/reports/daily", { params: asParams(range) }).then((r) => r.data);
export const fetchPatterns = (range = "week") =>
  API.get("/reports/patterns", { params: asParams(range) }).then((r) => r.data);
export const fetchOrderMix = (range = "week") =>
  API.get("/reports/order-mix", { params: asParams(range) }).then((r) => r.data);
export const fetchServiceSpeed = (range = "week") =>
  API.get("/reports/service-speed", { params: asParams(range) }).then((r) => r.data);
export const fetchGroupEvents = (range = "month") =>
  API.get("/reports/group-events", { params: asParams(range) }).then((r) => r.data);
export const fetchRevenueChannels = (range = "week") =>
  API.get("/reports/channels", { params: asParams(range) }).then((r) => r.data);
export const fetchCancellations = (range = "week") =>
  API.get("/reports/cancellations", { params: asParams(range) }).then((r) => r.data);
export const fetchEndOfDay = (params = {}) =>
  API.get("/reports/end-of-day", { params }).then((r) => r.data);

// --- F&B Cost Control ---------------------------------------------------
export const fetchIngredients = () => API.get("/cost/ingredients").then((r) => r.data);
export const createIngredient = (body) => API.post("/cost/ingredients", body).then((r) => r.data);
export const updateIngredient = (id, body) => API.patch(`/cost/ingredients/${id}`, body).then((r) => r.data);
export const deleteIngredient = (id) => API.delete(`/cost/ingredients/${id}`);
export const adjustIngredientStock = (id, body) => API.post(`/cost/ingredients/${id}/stock`, body).then((r) => r.data);
export const fetchRecipe = (menuId) => API.get(`/cost/menu/${menuId}/recipe`).then((r) => r.data);
export const setRecipe = (menuId, lines) => API.put(`/cost/menu/${menuId}/recipe`, lines).then((r) => r.data);
export const fetchCostTargets = () => API.get("/cost/targets").then((r) => r.data);
export const saveCostTargets = (body) => API.put("/cost/targets", body).then((r) => r.data);
export const logUsage = (body) => API.post("/cost/usage", body).then((r) => r.data);
export const fetchUsage = (on) => API.get("/cost/usage", { params: { on } }).then((r) => r.data);
export const fetchDailyIssue = (on) => API.get("/cost/issue", { params: { on } }).then((r) => r.data);
export const saveDailyIssue = (body) => API.post("/cost/issue", body).then((r) => r.data);
export const confirmDailyIssue = (body) => API.post("/cost/issue/confirm", body).then((r) => r.data);
export const fetchVariance = (params = {}) => API.get("/cost/variance", { params }).then((r) => r.data);
export const confirmUsage = (body) => API.post("/cost/usage/confirm", body).then((r) => r.data);
export const fetchStocktakes = () => API.get("/cost/stocktake").then((r) => r.data);
export const createStocktake = (body) => API.post("/cost/stocktake", body).then((r) => r.data);
export const fetchStocktake = (id) => API.get(`/cost/stocktake/${id}`).then((r) => r.data);
// --- Cost-control accuracy pack (waste log, data quality, price history) ---
export const logWaste = (body) => API.post("/cost/waste", body).then((r) => r.data);
export const fetchWaste = (params = {}) => API.get("/cost/waste", { params }).then((r) => r.data);
export const deleteWaste = (id) => API.delete(`/cost/waste/${id}`).then((r) => r.data);
export const fetchCostDataQuality = (params = {}) => API.get("/cost/data-quality", { params }).then((r) => r.data);
export const fetchIngredientPriceHistory = (id) => API.get(`/cost/ingredients/${id}/price-history`).then((r) => r.data);
export const fetchMenuEngineering = (params = {}) => API.get("/cost/menu-engineering", { params }).then((r) => r.data);
export const fetchReorderAlerts = () => API.get("/cost/reorder-alerts").then((r) => r.data);
export const fetchFoodCostTrend = (params = {}) => API.get("/cost/food-cost-trend", { params }).then((r) => r.data);
export const createReorderDraftPOs = (ingredientIds) => API.post("/cost/reorder/draft-po", ingredientIds ? { ingredient_ids: ingredientIds } : {}).then((r) => r.data);

// --- Food Recovery (event/group leftovers -> reusable pool, owner profit) ---
export const fetchRecoverableGroupOrders = () => API.get("/food-recovery/group-orders").then((r) => r.data);
export const fetchGroupOrderRecoveryItems = (gid) => API.get(`/food-recovery/group-orders/${gid}/items`).then((r) => r.data);
export const createFoodRecovery = (body) => API.post("/food-recovery", body).then((r) => r.data);
export const fetchFoodRecovery = (params = {}) => API.get("/food-recovery", { params }).then((r) => r.data);
export const reuseFoodRecovery = (id, servings) => API.post(`/food-recovery/${id}/reuse`, { servings }).then((r) => r.data);
export const deleteFoodRecovery = (id) => API.delete(`/food-recovery/${id}`).then((r) => r.data);
export const fetchFoodRecoveryReport = (params = {}) => API.get("/food-recovery/report", { params }).then((r) => r.data);

// --- Back Office (Store & Procurement) -------------------------------
export const boStores = () => API.get("/back-office/stores").then((r) => r.data);
export const boCreateStore = (body) => API.post("/back-office/stores", body).then((r) => r.data);
export const boUpdateStore = (id, body) => API.patch(`/back-office/stores/${id}`, body).then((r) => r.data);
export const boItems = (params = {}) => API.get("/back-office/items", { params }).then((r) => r.data);
export const boCreateItem = (body) => API.post("/back-office/items", body).then((r) => r.data);
export const boUpdateItem = (id, body) => API.patch(`/back-office/items/${id}`, body).then((r) => r.data);
export const boGetPolicy = () => API.get("/back-office/approval-policy").then((r) => r.data);
export const boSetPolicy = (body) => API.put("/back-office/approval-policy", body).then((r) => r.data);
export const boRequisitions = (params = {}) => API.get("/back-office/requisitions", { params }).then((r) => r.data);
export const boCreateRequisition = (body) => API.post("/back-office/requisitions", body).then((r) => r.data);
export const boGetRequisition = (id) => API.get(`/back-office/requisitions/${id}`).then((r) => r.data);
export const boSubmitRequisition = (id) => API.post(`/back-office/requisitions/${id}/submit`).then((r) => r.data);
export const boApproveRequisition = (id, note) => API.post(`/back-office/requisitions/${id}/approve`, { note }).then((r) => r.data);
export const boRejectRequisition = (id, note) => API.post(`/back-office/requisitions/${id}/reject`, { note }).then((r) => r.data);
export const boCreatePOFromReq = (id, supplierId) => API.post(`/back-office/requisitions/${id}/create-po`, supplierId ? { supplier_id: supplierId } : {}).then((r) => r.data);
export const boGoodsReceipts = () => API.get("/back-office/goods-receipts").then((r) => r.data);
export const boCreateGRN = (body) => API.post("/back-office/goods-receipts", body).then((r) => r.data);
export const boPostGRN = (id) => API.post(`/back-office/goods-receipts/${id}/post`).then((r) => r.data);
export const boTransfers = () => API.get("/back-office/transfers").then((r) => r.data);
export const boCreateTransfer = (body) => API.post("/back-office/transfers", body).then((r) => r.data);
export const boPostTransfer = (id) => API.post(`/back-office/transfers/${id}/post`).then((r) => r.data);
export const boIssues = () => API.get("/back-office/issues").then((r) => r.data);
export const boCreateIssue = (body) => API.post("/back-office/issues", body).then((r) => r.data);
export const boPostIssue = (id) => API.post(`/back-office/issues/${id}/post`).then((r) => r.data);
export const boStock = (params = {}) => API.get("/back-office/stock", { params }).then((r) => r.data);
export const boMovements = (params = {}) => API.get("/back-office/movements", { params }).then((r) => r.data);
export const boAdjustStock = (body) => API.post("/back-office/stock/adjust", body).then((r) => r.data);
export const boDashboard = () => API.get("/back-office/dashboard").then((r) => r.data);
export const boValuation = () => API.get("/back-office/reports/valuation").then((r) => r.data);
export const boLowStock = () => API.get("/back-office/reports/low-stock").then((r) => r.data);

// --- Purchasing (suppliers + purchase orders) ------------------------
export const fetchSuppliers = () => API.get("/purchasing/suppliers").then((r) => r.data);
export const createSupplier = (body) => API.post("/purchasing/suppliers", body).then((r) => r.data);
export const updateSupplier = (id, body) => API.patch(`/purchasing/suppliers/${id}`, body).then((r) => r.data);
export const fetchPurchaseOrders = (status) =>
  API.get("/purchasing/orders", { params: status ? { status } : {} }).then((r) => r.data);
export const fetchPurchaseOrder = (id) => API.get(`/purchasing/orders/${id}`).then((r) => r.data);
export const createPurchaseOrder = (body) => API.post("/purchasing/orders", body).then((r) => r.data);
export const updatePurchaseOrder = (id, body) => API.patch(`/purchasing/orders/${id}`, body).then((r) => r.data);
export const markPurchaseOrderOrdered = (id) => API.post(`/purchasing/orders/${id}/order`).then((r) => r.data);
export const receivePurchaseOrder = (id, body) => API.post(`/purchasing/orders/${id}/receive`, body).then((r) => r.data);
export const cancelPurchaseOrder = (id) => API.post(`/purchasing/orders/${id}/cancel`).then((r) => r.data);

// ---- Group / event orders -------------------------------------------------
export const fetchGroupOrders = (scope = "all") => API.get("/group-orders", { params: { scope } }).then((r) => r.data);
export const fetchGroupOrder = (id) => API.get(`/group-orders/${id}`).then((r) => r.data);
export const createGroupOrder = (body) => API.post("/group-orders", body).then((r) => r.data);
export const updateGroupOrder = (id, body) => API.patch(`/group-orders/${id}`, body).then((r) => r.data);
export const deleteGroupOrder = (id) => API.delete(`/group-orders/${id}`);
export const fetchGroupOrderPrep = (id) => API.get(`/group-orders/${id}/prep`).then((r) => r.data);
export const fetchBuffets = () => API.get("/group-orders/buffets").then((r) => r.data);
export const createBuffet = (body) => API.post("/group-orders/buffets", body).then((r) => r.data);
export const updateBuffet = (id, body) => API.patch(`/group-orders/buffets/${id}`, body).then((r) => r.data);
export const deleteBuffet = (id) => API.delete(`/group-orders/buffets/${id}`);
export const fetchMyGroupOrders = () => API.get("/group-orders/staff/mine").then((r) => r.data);
export const setGroupOrderStatus = (id, status) => API.post(`/group-orders/${id}/status`, { status }).then((r) => r.data);
export const fetchStationSpeed = (range = "week") =>
  API.get("/reports/station-speed", { params: asParams(range) }).then((r) => r.data);

export const fetchByTable = (range = "week") =>
  API.get("/reports/by-table", { params: asParams(range) }).then((r) => r.data);

export const fetchByStaff = (range = "week") =>
  API.get("/reports/by-staff", { params: asParams(range) }).then((r) => r.data);

export const fetchRecipePlan = (range = "week", limit = 10) =>
  API.get("/reports/recipe-plan", { params: { ...asParams(range), limit } }).then((r) => r.data);

// --- Billing (Stripe) ------------------------------------------------
// Returns { url } pointing at Stripe Checkout. Caller redirects to it.
export const startCheckout = (tier) =>
  API.post("/billing/checkout-session", { tier }).then((r) => r.data);

// Returns { url } for the Stripe Customer Portal (manage / cancel sub).
export const openBillingPortal = () =>
  API.post("/billing/portal").then((r) => r.data);

// --- Admin -----------------------------------------------------------
export const fetchAdminStats = () =>
  API.get("/admin/stats").then((r) => r.data);
export const fetchAdminAlerts = () =>
  API.get("/admin/alerts").then((r) => r.data);
export const fetchAdminTenants = ({ limit = 50, offset = 0, q, tier, sort } = {}) => {
  const params = { limit, offset };
  if (q) params.q = q;
  if (tier) params.tier = tier;
  if (sort) params.sort = sort;
  return API.get("/admin/tenants", { params }).then((r) => r.data);
};
export const fetchAdminTenantDetail = (tenantId) =>
  API.get(`/admin/tenants/${tenantId}`).then((r) => r.data);
export const adminSetTier = (tenantId, tier, note) =>
  API.patch(`/admin/tenants/${tenantId}/tier`, { tier, note }).then((r) => r.data);
// Replace the full set of à-la-carte add-ons for a tenant (idempotent).
export const adminSetAddons = (tenantId, addons) =>
  API.patch(`/admin/tenants/${tenantId}/addons`, { addons }).then((r) => r.data);
// One-click: give a single hotel its own owner login + dashboard (creates the
// owner org + links the hotel). Returns the owner login (password shown once).
export const adminEnableOwner = (tenantId, payload = {}) =>
  API.post(`/org/admin/restaurants/${tenantId}/enable-owner`, payload).then((r) => r.data);
// Reset a tenant's login password. Pass a password, or "" / falsy to
// have the server generate one. Returns { temporary_password, generated }.
export const adminResetPassword = (tenantId, newPassword) =>
  API.post(
    `/admin/tenants/${tenantId}/reset-password`,
    newPassword ? { new_password: newPassword } : {},
  ).then((r) => r.data);

// Admin-only Settings endpoints. Replaces ManagerBrandingPage --
// managers can no longer change country / languages / branding; the
// founder is the single write path. See backend/app/routes/admin.py
// TENANT SETTINGS section for the rationale.
export const fetchAdminTenantSettings = (tenantId) =>
  API.get(`/admin/tenants/${tenantId}/settings`).then((r) => r.data);

export const adminUpdateTenantSettings = (tenantId, patch) =>
  API.patch(`/admin/tenants/${tenantId}/settings`, patch).then((r) => r.data);

// --- Walkthrough leads (Founder HQ) ----------------------------------
// /admin/walkthroughs returns { counts, items } where items are inbound
// leads from the public marketing form on odra.app/#walkthrough.
export const fetchAdminWalkthroughs = ({ status, limit = 100 } = {}) => {
  const params = { limit };
  if (status) params.status = status;
  return API.get("/admin/walkthroughs", { params }).then((r) => r.data);
};

// Update a lead's status and/or admin notes. Both fields are optional;
// pass only the ones you want to change.
export const updateAdminWalkthrough = (leadId, { status, admin_notes } = {}) =>
  API.patch(`/admin/walkthroughs/${leadId}`, { status, admin_notes }).then(
    (r) => r.data,
  );

// Register a brand-new hotel/restaurant tenant (owner login). Admin-only.
export const adminCreateTenant = (body) =>
  API.post("/admin/tenants", body).then((r) => r.data);

// Convert a lead into a registered tenant (creates the login + marks won).
export const adminConvertLead = (leadId, body) =>
  API.post(`/admin/walkthroughs/${leadId}/convert`, body).then((r) => r.data);


// --- Recommendations -------------------------------------------------
// /menu/stats/top-sellers (manager + chef, auth required) -> 30-day
// top-selling items for the current tenant. Powers RecommendationsPage.
export const fetchTopSellers = () =>
  API.get("/menu/stats/top-sellers").then((r) => r.data);

// Quick helper to toggle is_chef_pick on a menu item from the
// recommendations page without re-sending all other fields.
export const setChefPick = (menuId, isPick, note = null) =>
  API.patch(`/menu/${menuId}`, {
    is_chef_pick: !!isPick,
    chef_pick_note: note,
  }).then((r) => r.data);

// --- Impersonation ---------------------------------------------------
// Localstorage keys for swap-back support.
const ADMIN_TOKEN_KEY = "menulink_admin_token";
const ADMIN_USER_KEY = "menulink_admin_user";
const ADMIN_ROLE_KEY = "menulink_admin_role";
const ADMIN_FEATURES_KEY = "menulink_admin_features";
const ADMIN_ADMIN_FLAG_KEY = "menulink_admin_is_admin";
const IMPERSONATION_META_KEY = "menulink_impersonating";

export const isImpersonating = () => {
  try { return !!localStorage.getItem(IMPERSONATION_META_KEY); }
  catch { return false; }
};
export const getImpersonationMeta = () => {
  try {
    const raw = localStorage.getItem(IMPERSONATION_META_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};

export const startImpersonation = async (tenantId) => {
  const res = await API.post(`/admin/tenants/${tenantId}/impersonate`);
  const { access_token, target, impersonator } = res.data;
  // Stash the admin's existing session under separate keys so we can
  // restore it on exit without re-login.
  const adminToken = localStorage.getItem(TOKEN_KEY);
  const adminUser = localStorage.getItem(USER_KEY);
  const adminRole = localStorage.getItem(ROLE_KEY);
  const adminFeatures = localStorage.getItem(FEATURES_KEY);
  const adminIsAdmin = localStorage.getItem(ADMIN_KEY);
  if (adminToken) localStorage.setItem(ADMIN_TOKEN_KEY, adminToken);
  if (adminUser) localStorage.setItem(ADMIN_USER_KEY, adminUser);
  if (adminRole) localStorage.setItem(ADMIN_ROLE_KEY, adminRole);
  if (adminFeatures) localStorage.setItem(ADMIN_FEATURES_KEY, adminFeatures);
  if (adminIsAdmin) localStorage.setItem(ADMIN_ADMIN_FLAG_KEY, adminIsAdmin);
  localStorage.setItem(IMPERSONATION_META_KEY, JSON.stringify({
    target_id: target.id,
    target_name: target.name,
    target_email: target.email,
    target_tier: target.subscription_tier,
    impersonator,
    started_at: new Date().toISOString(),
  }));
  // Swap in the impersonation token. The user object will be refreshed
  // via /auth/me after the page reloads, so we wipe the user blob now.
  localStorage.setItem(TOKEN_KEY, access_token);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(FEATURES_KEY);
  localStorage.setItem(ROLE_KEY, "manager");
  localStorage.setItem(ADMIN_KEY, "0"); // impersonating session is NOT admin
  return res.data;
};

export const exitImpersonation = () => {
  // Restore admin's original tokens + session.
  const adminToken = localStorage.getItem(ADMIN_TOKEN_KEY);
  const adminUser = localStorage.getItem(ADMIN_USER_KEY);
  const adminRole = localStorage.getItem(ADMIN_ROLE_KEY);
  const adminFeatures = localStorage.getItem(ADMIN_FEATURES_KEY);
  const adminIsAdmin = localStorage.getItem(ADMIN_ADMIN_FLAG_KEY);
  if (adminToken) localStorage.setItem(TOKEN_KEY, adminToken);
  if (adminUser) localStorage.setItem(USER_KEY, adminUser);
  if (adminRole) localStorage.setItem(ROLE_KEY, adminRole);
  if (adminFeatures) localStorage.setItem(FEATURES_KEY, adminFeatures);
  if (adminIsAdmin) localStorage.setItem(ADMIN_KEY, adminIsAdmin);
  localStorage.removeItem(ADMIN_TOKEN_KEY);
  localStorage.removeItem(ADMIN_USER_KEY);
  localStorage.removeItem(ADMIN_ROLE_KEY);
  localStorage.removeItem(ADMIN_FEATURES_KEY);
  localStorage.removeItem(ADMIN_ADMIN_FLAG_KEY);
  localStorage.removeItem(IMPERSONATION_META_KEY);
};

export default API;

// Open a WebSocket to the order broadcaster. ASYNC: it first mints a
// short-lived connect ticket over the authenticated REST channel, then opens
// the socket with `?ticket=` — so a full session JWT never rides in the URL
// (where it would leak into logs/history/Referer). Resolves to a `WebSocket`,
// or null if there's no session / the ticket couldn't be minted (caller then
// falls back to polling). The caller wires up onmessage / onclose.
export const openOrdersWebSocket = async () => {
  const token = getToken();
  if (!token) return null;
  let ticket;
  try {
    ticket = (await API.post("/ws/ticket")).data.ticket;
  } catch (e) {
    return null; // no ticket → caller stays on the polling fallback
  }
  if (!ticket) return null;
  const wsBase = API_URL.replace(/^http/, "ws").replace(/\/$/, "");
  const url = `${wsBase}/ws/orders?ticket=${encodeURIComponent(ticket)}`;
  return new WebSocket(url);
};

// --- Waiter <-> Table assignments (manager) ---------------------------
export const fetchAssignments = () =>
  API.get("/assignments/").then((r) => r.data);

export const fetchEffectiveRoster = (date) => {
  // date is a Date or YYYY-MM-DD string. Default = today (server-side).
  const params = {};
  if (date) {
    params.on = typeof date === "string" ? date : date.toISOString().slice(0, 10);
  }
  return API.get("/assignments/effective", { params }).then((r) => r.data);
};

// Waiter-facing: just MY tables for today (filtered server-side by the
// staff_id on the bearer token). Returns the same shape as fetchEffective
// but with only the tables assigned to the caller.
export const fetchMyAssignments = (date) => {
  const params = {};
  if (date) {
    params.on = typeof date === "string" ? date : date.toISOString().slice(0, 10);
  }
  return API.get("/assignments/mine", { params }).then((r) => r.data);
};

export const setDefaultAssignment = (tableId, staffId) =>
  API.post("/assignments/default", { table_id: tableId, staff_id: staffId })
    .then((r) => r.data);

export const setOverrideAssignment = (tableId, staffId, dateStr) =>
  API.post("/assignments/override", {
    table_id: tableId,
    staff_id: staffId,
    assignment_date: dateStr,
  }).then((r) => r.data);

export const deleteAssignment = (id) =>
  API.delete(`/assignments/${id}`);

// --- Scheduling (Hotel tier) ------------------------------------------
export const fetchShifts = (fromStr, toStr) =>
  API.get("/schedule/", { params: { from: fromStr, to: toStr } }).then((r) => r.data);
export const fetchMyShifts = (on) =>
  API.get("/schedule/mine", { params: { on } }).then((r) => r.data);

export const createShift = (payload) =>
  API.post("/schedule/", payload).then((r) => r.data);

export const updateShift = (id, payload) =>
  API.patch(`/schedule/${id}`, payload).then((r) => r.data);

export const deleteShift = (id) => API.delete(`/schedule/${id}`);

// --- Branding (Business tier) -----------------------------------------
export const fetchBranding = () =>
  API.get("/branding/").then((r) => r.data);

export const uploadLogo = async (file) => {
  const fd = new FormData();
  fd.append("file", file);
  const res = await API.post("/branding/logo", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
};

export const uploadCover = async (file) => {
  const fd = new FormData();
  fd.append("file", file);
  const res = await API.post("/branding/cover", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
};

export const clearLogo = () => API.delete("/branding/logo").then((r) => r.data);
export const clearCover = () => API.delete("/branding/cover").then((r) => r.data);

// --- Ratings (manager + customer) -------------------------------------
export const fetchRatingsByWaiter = (range = "week") =>
  API.get("/ratings/manager/by-waiter", { params: asParams(range) }).then((r) => r.data);

export const fetchRatingsByDish = (range = "week") =>
  API.get("/ratings/manager/by-dish", { params: asParams(range) }).then((r) => r.data);

export const fetchRatingsFeed = (limit = 50, range = "week") =>
  API.get("/ratings/manager/feed", { params: { limit, ...asParams(range) } }).then((r) => r.data);
export const fetchRatingsCoverage = (range = "week") =>
  API.get("/ratings/manager/coverage", { params: asParams(range) }).then((r) => r.data);

// --- Owner / Organization session (cross-branch dashboard) ------------
const ORG_TOKEN_KEY = "odra_org_token";
const ORG_INFO_KEY = "odra_org_info";
export const getOrgToken = () => localStorage.getItem(ORG_TOKEN_KEY);
export const getOrgInfo = () => {
  try { return JSON.parse(localStorage.getItem(ORG_INFO_KEY) || "null"); } catch (e) { return null; }
};
export const saveOrgSession = (token, organization) => {
  localStorage.setItem(ORG_TOKEN_KEY, token);
  localStorage.setItem(ORG_INFO_KEY, JSON.stringify(organization || null));
};
export const clearOrgSession = () => {
  localStorage.removeItem(ORG_TOKEN_KEY);
  localStorage.removeItem(ORG_INFO_KEY);
};

const ORG_API = axios.create({ baseURL: API_URL });
ORG_API.interceptors.request.use((config) => {
  const t = getOrgToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

export const orgLogin = (email, password) =>
  API.post("/auth/org/login", { email, password }).then((r) => r.data);
export const fetchOrgSummary = (fromStr, toStr) =>
  ORG_API.get("/org/summary", { params: { from: fromStr, to: toStr } }).then((r) => r.data);
export const fetchOrgFrontDesk = (fromStr, toStr) =>
  ORG_API.get("/org/frontdesk", { params: { from: fromStr, to: toStr } }).then((r) => r.data);
export const fetchOrgWellness = ({ from, to } = {}) =>
  ORG_API.get("/org/wellness", { params: { from, to } }).then((r) => r.data);
export const fetchOrgTrend = (fromStr, toStr, branch) =>
  ORG_API.get("/org/trend", { params: { from: fromStr, to: toStr, ...(branch ? { branch } : {}) } }).then((r) => r.data);
export const fetchOrgBranchTopItems = (branchId, fromStr, toStr) =>
  ORG_API.get(`/org/branch/${branchId}/top-items`, { params: { from: fromStr, to: toStr } }).then((r) => r.data);
export const fetchOrgSlowItems = (branchId, fromStr, toStr) =>
  ORG_API.get(`/org/branch/${branchId}/slow-items`, { params: { from: fromStr, to: toStr } }).then((r) => r.data);
export const fetchOrgLowRated = (branchId, fromStr, toStr) =>
  ORG_API.get(`/org/branch/${branchId}/low-rated-items`, { params: { from: fromStr, to: toStr } }).then((r) => r.data);
export const fetchOrgRatingsTrend = (bucket) =>
  ORG_API.get("/org/ratings-trend", { params: { bucket } }).then((r) => r.data);
export const fetchOrgPatterns = (fromStr, toStr, branch) =>
  ORG_API.get("/org/patterns", { params: { from: fromStr, to: toStr, ...(branch ? { branch } : {}) } }).then((r) => r.data);
export const fetchOrgCompareItems = (fromStr, toStr) =>
  ORG_API.get("/org/compare-items", { params: { from: fromStr, to: toStr } }).then((r) => r.data);
export const fetchOrgServiceSpeed = (fromStr, toStr) =>
  ORG_API.get("/org/service-speed", { params: { from: fromStr, to: toStr } }).then((r) => r.data);
export const fetchOrgStationSpeed = (fromStr, toStr) =>
  ORG_API.get("/org/station-speed", { params: { from: fromStr, to: toStr } }).then((r) => r.data);
export const fetchOrgAlerts = (days = 7) =>
  ORG_API.get("/org/alerts", { params: { days } }).then((r) => r.data);
export const fetchOrgMenuEngineering = (branchId, fromStr, toStr) =>
  ORG_API.get(`/org/branch/${branchId}/menu-engineering`, { params: { from: fromStr, to: toStr } }).then((r) => r.data);

// --- Admin: group (organization) management ---------------------------
export const adminListOrgs = () =>
  API.get("/org/admin/organizations").then((r) => r.data);
export const adminCreateOrg = (payload) =>
  API.post("/org/admin/organizations", payload).then((r) => r.data);
export const adminUpdateOrg = (orgId, payload) =>
  API.patch(`/org/admin/organizations/${orgId}`, payload).then((r) => r.data);
export const adminUploadOrgLogo = async (orgId, file) => {
  const fd = new FormData();
  fd.append("file", file);
  const res = await API.post(`/org/admin/organizations/${orgId}/logo`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
};
export const adminUploadOrgCover = async (orgId, file) => {
  const fd = new FormData();
  fd.append("file", file);
  const res = await API.post(`/org/admin/organizations/${orgId}/cover`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
};
export const adminListOrgBranches = (orgId) =>
  API.get(`/org/admin/organizations/${orgId}/branches`).then((r) => r.data);
export const adminAttachBranch = (orgId, restaurantId) =>
  API.post(`/org/admin/organizations/${orgId}/attach/${restaurantId}`).then((r) => r.data);
export const adminDetachBranch = (orgId, restaurantId) =>
  API.post(`/org/admin/organizations/${orgId}/detach/${restaurantId}`).then((r) => r.data);

// --- Shared device (4-char PIN quick-switch) -------------------------
const DEVICE_TOKEN_KEY = "menulink_device_token";
const DEVICE_REST_KEY = "menulink_device_rest";

export const getDeviceToken = () => localStorage.getItem(DEVICE_TOKEN_KEY);
export const isDeviceMode = () => !!getDeviceToken();
export const getDeviceRestaurant = () => {
  try { return JSON.parse(localStorage.getItem(DEVICE_REST_KEY) || "null"); } catch { return null; }
};
export const clearDeviceSession = () => {
  localStorage.removeItem(DEVICE_TOKEN_KEY);
  localStorage.removeItem(DEVICE_REST_KEY);
};

// Manager turns the current tablet into a shared device (manager token).
export const createDeviceSession = async () => {
  const res = await API.post("/auth/device");
  localStorage.setItem(DEVICE_TOKEN_KEY, res.data.device_token);
  localStorage.setItem(DEVICE_REST_KEY, JSON.stringify(res.data.restaurant || null));
  return res.data;
};

export const revokeDevices = () => API.post("/auth/device/revoke");

// Switch to a staff member by their 4-char code (device token).
// Raw axios so a wrong-code 401 is handled locally, not the global redirect.
export const pinLogin = async (pin) => {
  const dt = getDeviceToken();
  if (!dt) throw new Error("No device session");
  const res = await axios.post(
    `${API_URL.replace(/\/$/, "")}/auth/pin`,
    { pin: (pin || "").toUpperCase() },
    { headers: { Authorization: `Bearer ${dt}` } }
  );
  localStorage.setItem(TOKEN_KEY, res.data.access_token);
  localStorage.setItem(ROLE_KEY, res.data.role);
  localStorage.setItem(FEATURES_KEY, JSON.stringify(res.data.features || []));
  localStorage.setItem(ADMIN_KEY, "0");
  localStorage.setItem(STAFF_KEY, JSON.stringify({
    name: res.data.staff_name || null,
    photo_url: res.data.staff_photo_url || null,
    role: res.data.role || null,
  }));
  try { await refreshMe(); } catch (e) { /* restaurant context fills lazily */ }
  return res.data;
};

// Lock back to the PIN screen: drop the staff session, keep the device.
export const lockToPinScreen = () => {
  [TOKEN_KEY, USER_KEY, ROLE_KEY, FEATURES_KEY, ADMIN_KEY, STAFF_KEY].forEach((k) => localStorage.removeItem(k));
};

export const setMyPin = (pin) => API.post("/staff/me/pin", { pin: (pin || "").toUpperCase() });
export const resetStaffPin = (staffId) => API.post(`/staff/${staffId}/pin/reset`);
